import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, Clock, Mail, MessageCircle, MonitorPlay, Phone, Send } from 'lucide-react';
import styles from './Demo.module.css';
import Button from '../../components/Button/Button';
import { SITE, waLink } from '../../config/site';
import Seo, { breadcrumbLd } from '../../components/Seo/Seo';

const EMPTY = { name: '', salon: '', city: '', phone: '', branches: '1', message: '' };

export default function Demo() {
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [sent, setSent] = useState(false);

    const set = (k) => (e) => {
        const v = k === 'phone' ? e.target.value.replace(/\D/g, '').slice(0, 10) : e.target.value;
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((er) => ({ ...er, [k]: '' }));
    };

    const message = () => [
        'Hi Hirsta, I would like a demo.',
        `Name: ${form.name}`,
        `Salon: ${form.salon}`,
        form.city && `City: ${form.city}`,
        `Phone: ${form.phone}`,
        `Branches: ${form.branches}`,
        form.message && `Note: ${form.message}`,
    ].filter(Boolean).join('\n');

    const submit = (e) => {
        e.preventDefault();
        const er = {};
        if (!form.name.trim()) er.name = 'Enter your name.';
        if (!form.salon.trim()) er.salon = 'Enter your salon name.';
        if (form.phone.length !== 10) er.phone = 'Enter a 10-digit mobile number.';
        setErrors(er);
        if (Object.keys(er).length) return;
        window.open(waLink(message()), '_blank', 'noopener');
        setSent(true);
    };

    return (
        <section className={styles.page}>
            <Seo
                title="Book a free salon software demo"
                description="See Hirsta on your own salon's services, staff and prices in a free 20-minute demo. Billing, memberships, packages, appointments and WhatsApp marketing."
                jsonLd={[breadcrumbLd([['Home', '/'], ['Book a demo', '/demo']])]}
            />
            <div className={styles.inner}>
                <div className={styles.info}>
                    <h1>See Hirsta on your own salon</h1>
                    <p className={styles.lead}>Tell us a little about your salon. We will set up a demo with your services, staff and prices, and walk you through it.</p>
                    <ul className={styles.list}>
                        <li><MonitorPlay size={16} /> <span><strong>A live walkthrough</strong> billing, memberships, packages, appointments and reports</span></li>
                        <li><Clock size={16} /> <span><strong>About 20 minutes</strong> on a call or video, at a time that suits you</span></li>
                        <li><CheckCircle2 size={16} /> <span><strong>No setup on your side</strong> we prepare everything before the call</span></li>
                    </ul>
                    <div className={styles.contact}>
                        <a href={`tel:${SITE.phone.replace(/\s/g, '')}`}><Phone size={14} /> {SITE.phone}</a>
                        <a href={waLink('Hi, I would like a demo of Hirsta.')} target="_blank" rel="noreferrer"><MessageCircle size={14} /> WhatsApp</a>
                        <a href={`mailto:${SITE.email}`}><Mail size={14} /> {SITE.email}</a>
                    </div>
                </div>

                <div className={styles.formCard}>
                    <AnimatePresence mode="wait">
                        {sent ? (
                            <motion.div key="done" className={styles.done} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}>
                                <span className={styles.doneIcon}><CheckCircle2 size={28} /></span>
                                <h2>Your request is ready to send</h2>
                                <p>WhatsApp opened with your details. Tap send there, and we will reply to fix a time.</p>
                                <div className={styles.doneActions}>
                                    <Button href={waLink(message())} icon={MessageCircle}>Open WhatsApp again</Button>
                                    <Button href={`mailto:${SITE.email}?subject=${encodeURIComponent('Hirsta demo request')}&body=${encodeURIComponent(message())}`} variant="ghost" icon={Mail}>Send by email instead</Button>
                                </div>
                                <button type="button" className={styles.reset} onClick={() => { setSent(false); setForm(EMPTY); }}>Start over</button>
                            </motion.div>
                        ) : (
                            <motion.form key="form" onSubmit={submit} noValidate initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                <h2>Book a free demo</h2>
                                <div className={styles.grid}>
                                    <Field label="Your name" error={errors.name}><input value={form.name} onChange={set('name')} placeholder="e.g. Rahul Verma" autoComplete="name" /></Field>
                                    <Field label="Salon name" error={errors.salon}><input value={form.salon} onChange={set('salon')} placeholder="e.g. Glow Studio" /></Field>
                                    <Field label="Mobile number" error={errors.phone}><input value={form.phone} onChange={set('phone')} placeholder="10-digit mobile" inputMode="numeric" autoComplete="tel" /></Field>
                                    <Field label="City"><input value={form.city} onChange={set('city')} placeholder="e.g. Noida" /></Field>
                                    <Field label="Number of branches">
                                        <select value={form.branches} onChange={set('branches')}>
                                            <option value="1">1</option><option value="2-3">2 to 3</option><option value="4-10">4 to 10</option><option value="10+">More than 10</option>
                                        </select>
                                    </Field>
                                    <Field label="Anything we should know?" full><textarea rows={3} value={form.message} onChange={set('message')} placeholder="e.g. We sell a lot of packages and want staff incentives" /></Field>
                                </div>
                                <Button type="submit" size="lg" icon={Send} className={styles.submit}>Send request on WhatsApp</Button>
                                <p className={styles.privacy}>We use these details only to arrange your demo.</p>
                            </motion.form>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
}

function Field({ label, error, full, children }) {
    return (
        <label className={`${styles.field} ${full ? styles.full : ''} ${error ? styles.hasError : ''}`}>
            <span>{label}</span>
            {children}
            {error && <em>{error}</em>}
        </label>
    );
}
